import { useEffect, useState, type ComponentType, type ReactElement } from 'react';
import { Alert, Badge, Box, Center, Group, Loader, SimpleGrid, Stack, Tabs, Text } from '@mantine/core';
import {
    Activity,
    AlertCircle,
    AlertTriangle,
    BarChart3,
    Boxes,
    CircleCheck,
    CreditCard,
    HeartPulse,
    KeyRound,
    Plug,
    Radio,
    ShieldCheck,
    Users,
} from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import StatBand from '@/design/StatBand';
import AuditLogView from '@/features/audit/AuditLogView';
import { useAuth } from '@/features/auth/AuthContext';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { SystemHealthViewModel } from './SystemHealthViewModel';
import type { HealthStatus, PlatformServiceHealth } from './SystemHealthService';

const STATUS_MANTINE: Record<HealthStatus, string> = {
    healthy: 'teal',
    degraded: 'yellow',
    unhealthy: 'red',
};

const STATUS_LABEL: Record<HealthStatus, string> = {
    healthy: 'Operational',
    degraded: 'Degraded',
    unhealthy: 'Disrupted',
};

const SERVICE_ICONS: Record<string, ComponentType<{ size?: number; color?: string }>> = {
    auth: ShieldCheck,
    directory: Users,
    connections: Plug,
    licenses: KeyRound,
    telemetry: Radio,
    insights: BarChart3,
    billing: CreditCard,
};

/**
 * A SaaS-appropriate operational rollup — a single status banner without naming
 * internal services (tenants can't act on individual microservices; the detailed
 * per-service grid lives on the self-hosted Health tab).
 */
function OperationalRollup({ overall }: { overall: HealthStatus }): ReactElement {
    if (overall === 'healthy') {
        return (
            <Alert variant="light" color="teal" icon={<CircleCheck size={16} />} title="All systems operational">
                All Ultra Trace services are running normally.
            </Alert>
        );
    }
    const color = overall === 'unhealthy' ? 'red' : 'yellow';
    const title = overall === 'unhealthy' ? 'Service disruption' : 'Partial service disruption';
    const body =
        overall === 'unhealthy'
            ? 'Some features are temporarily unavailable. Our team is investigating.'
            : 'Some features may be slow or temporarily unavailable.';
    return (
        <Alert variant="light" color={color} icon={<AlertTriangle size={16} />} title={title}>
            {body}
        </Alert>
    );
}

function ServiceHealthCard({ service }: { service: PlatformServiceHealth }): ReactElement {
    const c = STATUS_MANTINE[service.status];
    const solid = `var(--mantine-color-${c}-6)`;
    const soft = `var(--mantine-color-${c}-light)`;
    const border = `var(--mantine-color-${c}-light-hover)`;
    const Icon = SERVICE_ICONS[service.key] ?? Boxes;
    return (
        <SurfaceCard $padding="md" $static style={{ height: '100%', borderTop: `2px solid ${solid}` }}>
            <Group justify="space-between" align="center" mb="sm">
                <Box
                    style={{
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: soft,
                        border: `1px solid ${border}`,
                        borderRadius: 'var(--bm-radius)',
                    }}
                >
                    <Icon size={15} color={solid} />
                </Box>
                <Badge variant="light" color={c} size="sm">
                    {STATUS_LABEL[service.status]}
                </Badge>
            </Group>
            <Text fw={700} style={{ fontSize: '16px', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                {service.name}
            </Text>
            <Text size="xs" c="dimmed" mt={6} lh={1.5}>
                {service.detail}
            </Text>
            <Text size="xs" c="dimmed" fw={500} mt="md" style={{ fontSize: '11px' }}>
                {service.status === 'unhealthy' ? 'No response' : `${service.latencyMs}ms readiness probe`}
            </Text>
        </SurfaceCard>
    );
}

/**
 * Overview shown to every tenant: deployment facts and a single operational
 * rollup. No per-service breakdown — that's ops detail on the self-hosted Health
 * tab, not something a managed-SaaS tenant can act on.
 */
function SystemOverview({ vm }: { vm: SystemHealthViewModel }): ReactElement {
    if (vm.loading && !vm.data) {
        return (
            <Center py="xl">
                <Loader />
            </Center>
        );
    }
    if (!vm.data) {
        return (
            <Alert variant="light" color="red" icon={<AlertCircle size={16} />}>
                {vm.error ?? 'Could not load system status.'}
            </Alert>
        );
    }

    const { data } = vm;
    return (
        <Stack gap="lg">
            <StatBand
                stats={[
                    { label: 'Mode', value: data.mode === 'saas' ? 'SaaS' : 'Self-hosted', hint: data.mode === 'saas' ? 'managed cloud' : 'you manage upgrades' },
                    { label: 'Version', value: data.version, hint: 'stable channel' },
                    { label: 'Status', value: STATUS_LABEL[data.overall], hint: `checked ${new Date(data.checkedAt).toLocaleTimeString()}` },
                ]}
            />
            <OperationalRollup overall={data.overall} />
        </Stack>
    );
}

/**
 * Self-hosted-only Health tab: the live per-service readiness grid. Operators of a
 * self-hosted instance manage their own infrastructure, so this detail is
 * actionable for them (unlike a managed-SaaS tenant).
 */
function SystemHealthGrid({ services }: { services: PlatformServiceHealth[] }): ReactElement {
    const healthyCount = services.filter((s) => s.status === 'healthy').length;
    const needAttention = services.length - healthyCount;
    return (
        <Stack gap="lg">
            <StatBand
                stats={[
                    {
                        label: 'Services',
                        value: `${healthyCount}/${services.length}`,
                        hint: needAttention === 0 ? 'all operational' : `${needAttention} need attention`,
                    },
                ]}
            />
            <Box>
                <Text size="sm" fw={600}>
                    Infrastructure health
                </Text>
                <Text size="xs" c="dimmed" mb="sm">
                    Live readiness of core services and dependencies.
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    {services.map((s) => (
                        <ServiceHealthCard key={s.key} service={s} />
                    ))}
                </SimpleGrid>
            </Box>
        </Stack>
    );
}

/**
 * The tenant System settings page: deployment facts + an operational rollup
 * (Overview), a self-hosted-only per-service Health tab, and (for `settings.view`)
 * the organization's audit trail (SAAS-29).
 */
export default function SystemSettingsPage(): ReactElement {
    const [tab, setTab] = useState<string | null>('overview');
    const { hasPermission } = useAuth();
    const canViewAudit = hasPermission({ action: 'view', resource: 'settings' });
    const vm = useViewModel(SystemHealthViewModel);
    useEffect(() => vm.init(), [vm]);

    const showHealthTab = vm.data?.mode === 'self-hosted';

    return (
        <Box>
            <PageHeaderComponent
                title="System"
                subtitle="Deployment, version, service status, and the organization audit trail."
            />

            <Tabs value={tab} onChange={setTab} keepMounted={false}>
                <Tabs.List mb="xl">
                    <Tabs.Tab value="overview" leftSection={<Activity size={14} />}>
                        Overview
                    </Tabs.Tab>
                    {showHealthTab && (
                        <Tabs.Tab value="health" leftSection={<HeartPulse size={14} />}>
                            Health
                        </Tabs.Tab>
                    )}
                    {canViewAudit && (
                        <Tabs.Tab value="audit" leftSection={<ShieldCheck size={14} />}>
                            Audit
                        </Tabs.Tab>
                    )}
                </Tabs.List>

                <Tabs.Panel value="overview">
                    <SystemOverview vm={vm} />
                </Tabs.Panel>

                {showHealthTab && vm.data && (
                    <Tabs.Panel value="health">
                        <SystemHealthGrid services={vm.data.services} />
                    </Tabs.Panel>
                )}

                {canViewAudit && (
                    <Tabs.Panel value="audit">
                        <Box>
                            <Text size="sm" fw={600}>
                                Audit trail
                            </Text>
                            <Text size="xs" c="dimmed" mb="md">
                                Privileged actions taken on your organization — by your admins and by platform
                                support. Filter by actor, action, resource or date.
                            </Text>
                            <AuditLogView scope="tenant" />
                        </Box>
                    </Tabs.Panel>
                )}
            </Tabs>
        </Box>
    );
}
